import React, { type JSX } from 'react'
import { Observable } from 'rx'
import { isStartingPriceSelection } from '@mobi/betslip/helpers/typeGuards'
import {
  SuperPickState,
  state$ as superPickState$,
} from '@core/Areas/Quickbet/Components/SuperPick/Components/SuperPick/driver'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { calculateProjectedReturn } from '@core/Areas/Quickbet/helpers/calculator'
import { observeImmutable } from '@core/Components/HOCs'
import type { BetSpecialOffer } from '@core/Utils/betting/superPicks/models/BetSpecialOffer'
import { InvestmentState, state$ as investmentState$ } from '../../driver'
import { ProjectedReturnMoneyStyled, ProjectedReturnStyled } from './ProjectedReturn.styles'

interface ProjectedReturnProps {
  value: number | null
}

const projectedReturn$ = Observable.combineLatest(
  quickbetState$,
  investmentState$,
  superPickState$,
  (quickbet, investment, superPick): ProjectedReturnProps => {
    const investmentJs = investment.toJS() as InvestmentState

    const {
      win: { value: investmentWin },
      place: { value: investmentPlace },
    } = investmentJs

    const bonusBetValue = investmentJs.bonusBet?.value

    let selectedSpecialOffer: BetSpecialOffer | null = null

    if (superPick.specialOffers && superPick.selectedSuperPickSeq) {
      selectedSpecialOffer =
        (superPick.toJS() as SuperPickState).specialOffers.find(
          offer => offer.specialSeq === superPick.selectedSuperPickSeq
        ) ?? null
    }
    const quickbetState: QuickbetState = quickbet.toJS()
    const hasNoReturn =
      !!quickbetState.selection && isStartingPriceSelection(quickbetState.selection)

    if (hasNoReturn) {
      return { value: null }
    }

    return {
      value:
        investmentWin + investmentPlace <= 0
          ? 0
          : calculateProjectedReturn(
              quickbet.bettingType,
              (quickbetState.selection as { winPrice: number }).winPrice,
              (quickbetState.selection as { placePrice: number }).placePrice,
              investmentWin,
              investmentPlace,
              quickbet.isEachWay,
              bonusBetValue,
              selectedSpecialOffer
            ),
    }
  }
)

export const ProjectedReturnComponent = ({ value }: ProjectedReturnProps): JSX.Element => {
  return (
    <ProjectedReturnStyled data-testid='Quickbet.ProjectedReturn'>
      {value !== null && (
        <>
          Projected Return: <ProjectedReturnMoneyStyled amount={value} />
        </>
      )}
    </ProjectedReturnStyled>
  )
}

export const ProjectedReturn = observeImmutable<ProjectedReturnProps, object>(
  projectedReturn$,
  ({ record }) => <ProjectedReturnComponent {...record} />
)
